import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import CircularProgress from "@material-ui/core/CircularProgress";
import { Grid } from "react-loader-spinner";
import { grayColor, whiteColor } from "assets/jss/material-dashboard-pro-react";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: (props) => props.minHeight || "400px",
    width: "100%",
    backgroundColor: whiteColor,
    borderRadius: "8px",
    transition: "opacity 0.3s ease-in-out",
  },
  loaderWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "20px",
  },
  message: {
    fontSize: "14px",
    color: grayColor[4],
    textAlign: "center",
    maxWidth: "300px",
  },
  overlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(255, 255, 255, 0.85)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "8px",
    zIndex: 10,
  },
  hidden: {
    opacity: 0,
    pointerEvents: "none",
  },
});

/**
 * SectionLoader - A loading indicator for sections in the Alignment Session view.
 *
 * Can be used in two modes:
 * 1. Standalone (default) - Takes up the full section space while loading
 * 2. Overlay mode - Overlays on top of existing content during data refresh
 *
 * @param {Object} props
 * @param {boolean} props.loading - Whether the section is currently loading
 * @param {string} props.message - Custom loading message (optional)
 * @param {string} props.clientColor - Theme color for the loader
 * @param {boolean} props.overlay - If true, shows as an overlay instead of replacing content
 * @param {string} props.variant - "grid" | "circular" - The type of spinner to show
 * @param {string} props.minHeight - Minimum height for standalone mode
 */
function SectionLoader({
  loading,
  message,
  clientColor,
  overlay = false,
  variant = "grid",
  minHeight = "400px",
}) {
  const classes = useStyles({ minHeight });
  const { t } = useTranslation();

  const defaultMessage =
    t("alignment_session.loading_section") || "Carregando dados...";

  const renderLoader = () => (
    <div className={classes.loaderWrapper}>
      {variant === "grid" ? (
        <Grid width={40} height={40} color={clientColor} />
      ) : (
        <CircularProgress style={{ color: clientColor }} size={40} />
      )}
      <span className={classes.message}>{message || defaultMessage}</span>
    </div>
  );

  if (!loading && !overlay) {
    return null;
  }

  if (overlay) {
    return (
      <div className={`${classes.overlay} ${!loading ? classes.hidden : ""}`}>
        {renderLoader()}
      </div>
    );
  }

  return <div className={classes.root}>{renderLoader()}</div>;
}

SectionLoader.propTypes = {
  loading: PropTypes.bool.isRequired,
  message: PropTypes.string,
  clientColor: PropTypes.string,
  overlay: PropTypes.bool,
  variant: PropTypes.oneOf(["grid", "circular"]),
  minHeight: PropTypes.string,
};

SectionLoader.defaultProps = {
  message: null,
  clientColor: "#4caf50",
  overlay: false,
  variant: "grid",
  minHeight: "400px",
};

export default SectionLoader;
